import { useState } from "react";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";
import type { PickerCellEditor } from "@design-system/Picker/Picker.lib";
import { SEARCH_DEBOUNCE_TIME } from "@constants";
import { useCounterpartyPickerQuery } from "@api/counterparty.api";

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * The counterparty picker's debounced server search. A grid cell editor picks
 * the first row on Enter / Tab, so until the typed search's results arrive it
 * lists nothing pickable (`isCellSearchPending`), and while a search is typed
 * the clear row steps aside (`isCellSearching`).
 */
export const useCounterpartyComboboxSearch = ({
    isOpen,
    cellEditor,
}: {
    isOpen: boolean;
    cellEditor?: PickerCellEditor;
}) => {
    const activeWorkspace = useActiveWorkspace();
    const [typedSearch, setTypedSearch] = useState<string>();
    const [search, setSearch] = useState<string>();

    useDebouncedEffect(
        () => {
            setSearch(typedSearch);
        },
        [typedSearch],
        SEARCH_DEBOUNCE_TIME,
    );

    const counterpartiesQuery = useCounterpartyPickerQuery(
        {
            search,
            sort: DirectorySort.NAME_ASC,
            includeRegistered: search
                ? RegisteredCounterpartyIncludeScope.ALL
                : RegisteredCounterpartyIncludeScope.USED,
        },
        {
            staleTime: !search ? FIVE_MINUTES : undefined,
            enabled: !!activeWorkspace && isOpen,
        },
    );

    const isCellEditor = cellEditor !== undefined;
    const isCellSearchPending =
        isCellEditor &&
        (typedSearch !== search || counterpartiesQuery.isLoading);

    return {
        typedSearch,
        setTypedSearch,
        counterpartiesQuery,
        isLoading: counterpartiesQuery.isLoading || isCellSearchPending,
        isCellSearching: isCellEditor && (typedSearch ?? "").trim() !== "",
        isCellSearchPending,
    };
};
